import { subscriptionService } from '../apis/subscriptionService';
import { toast } from 'react-hot-toast';

const POST_PAYMENT_SCRIBE_KEY = 'vbot:postPaymentScribeRefresh';
const PAYMENT_COMPLETED_TOAST_KEY = 'vbot:paymentCompletedToast';

/** In-memory guard for React Strict Mode double-mount in the same session. */
const paymentCompletedToastsShown = new Set();

/** Remove stuck Radix overlays / scroll locks after payment flows. */
export function cleanupStaleUiOverlays() {
  if (typeof document === 'undefined') return;

  document.body.style.pointerEvents = '';
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
  document.body.removeAttribute('data-scroll-locked');
  document.documentElement.removeAttribute('data-scroll-locked');

  document
    .querySelectorAll('[data-radix-dialog-overlay], [data-radix-popper-content-wrapper]')
    .forEach((node) => node.remove());
}

export function markPostPaymentScribeRefresh() {
  try {
    sessionStorage.setItem(POST_PAYMENT_SCRIBE_KEY, String(Date.now()));
  } catch {
    // ignore storage failures
  }
}

export function consumePostPaymentScribeRefresh() {
  try {
    const value = sessionStorage.getItem(POST_PAYMENT_SCRIBE_KEY);
    sessionStorage.removeItem(POST_PAYMENT_SCRIBE_KEY);
    return Boolean(value);
  } catch {
    return false;
  }
}

/** Show the payment-success toast at most once per payment (survives Strict Mode remounts). */
export function showPaymentCompletedToastOnce(paymentId) {
  if (!paymentId) return false;

  const id = String(paymentId);
  if (paymentCompletedToastsShown.has(id)) return false;

  try {
    const storageKey = `${PAYMENT_COMPLETED_TOAST_KEY}:${id}`;
    if (sessionStorage.getItem(storageKey)) {
      paymentCompletedToastsShown.add(id);
      return false;
    }
    sessionStorage.setItem(storageKey, String(Date.now()));
  } catch {
    // fall through — still dedupe in-memory for this tab
  }

  paymentCompletedToastsShown.add(id);
  toast.success('Payment completed!');
  return true;
}

/** Clear caches and link payment to premium subscription when possible. */
export async function prepareSubscriptionAfterPayment(paymentId) {
  subscriptionService.clearSubscriptionCache();
  if (!paymentId) return;

  try {
    await subscriptionService.upgradeToPremium(paymentId);
  } catch (error) {
    console.warn('Subscription upgrade after payment:', error);
  }
}

/** Full reload into scribe so trial/subscription state matches payment. */
export function navigateToScribeAfterPayment() {
  cleanupStaleUiOverlays();
  markPostPaymentScribeRefresh();
  const target = `${window.location.origin}/scribe`;
  try {
    window.location.replace(target);
  } catch {
    window.location.href = target;
  }
}
